import type { DisplayOptions } from "../store/prefs";
import { checkbox, el, field, selectOf } from "./dom";

// The Display drawer's sight-reading-staff controls (enable + the three dials),
// extracted from controls.ts to keep that glue file under the size cap. A small
// self-contained unit: build the elements, read them as DisplayOptions fields,
// reflect persisted values, and bind a change handler.

type StaffFields = Pick<
  DisplayOptions,
  | "staffEnabled"
  | "staffSplit"
  | "highwayOpacity"
  | "staffRows"
  | "staffFollow"
  | "showChordSymbols"
  | "challengeFade"
>;

interface StaffControls {
  /** Drawer rows to splice into the Display section. */
  rows: HTMLElement[];
  /** Current control values as DisplayOptions staff fields. */
  read(): StaffFields;
  /** Reflect persisted values into the controls. */
  set(display: DisplayOptions): void;
  /** Fire `onChange` whenever any staff control changes. */
  bind(onChange: () => void): void;
}

export function buildStaffControls(): StaffControls {
  const enabled = checkbox("Sight-reading staff");
  const chordSyms = checkbox("Lead-sheet chords");
  const challenge = checkbox("Fade as I master (challenge)");
  const split = selectOf([
    ["0", "Staff off (100/0)"],
    ["1", "Highway-heavy (70/30)"],
    ["2", "Even (50/50)"],
    ["3", "Staff-heavy (30/70)"],
    ["4", "Staff only (0/100)"],
  ]);
  const rows = selectOf([
    ["1", "1 row"],
    ["2", "2 rows"],
    ["3", "3 rows"],
    ["4", "4 rows"],
    ["0", "Fit page"],
  ]);
  const follow = selectOf([
    ["auto", "Auto"],
    ["flow", "Flow (scroll)"],
    ["page", "Page (sweep)"],
  ]);
  // Reading crutch: dims the falling-note highway (100 = full crutch, 0 = read
  // from the staff alone) independently of the staff split.
  const crutch = el<HTMLInputElement>("input", {
    type: "range",
    min: "0",
    max: "100",
    step: "5",
    value: "100",
  });

  return {
    rows: [
      enabled.wrap,
      chordSyms.wrap,
      field("Staff split", split),
      field("Highway crutch", crutch),
      challenge.wrap,
      field("Staff rows", rows),
      field("Staff follow", follow),
    ],
    read: () => ({
      staffEnabled: enabled.input.checked,
      showChordSymbols: chordSyms.input.checked,
      challengeFade: challenge.input.checked,
      staffSplit: Number(split.value),
      highwayOpacity: Number(crutch.value) / 100,
      staffRows: Number(rows.value),
      staffFollow: ["flow", "page"].includes(follow.value)
        ? (follow.value as DisplayOptions["staffFollow"])
        : "auto",
    }),
    set: (display) => {
      enabled.input.checked = display.staffEnabled;
      chordSyms.input.checked = display.showChordSymbols;
      challenge.input.checked = display.challengeFade;
      split.value = String(display.staffSplit);
      crutch.value = String(Math.round(display.highwayOpacity * 100));
      rows.value = String(display.staffRows);
      follow.value = display.staffFollow;
    },
    bind: (onChange) => {
      enabled.input.onchange = onChange;
      chordSyms.input.onchange = onChange;
      challenge.input.onchange = onChange;
      crutch.oninput = onChange;
      for (const s of [split, rows, follow]) s.onchange = onChange;
    },
  };
}
